import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Factory,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Save,
  Edit3,
  ChevronDown
} from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, getDocuments } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { ProductionService } from '../services/productionService';
import { ProductionSummaryRow } from '../types/inventory';

interface ProductionPageProps {
  onBack: () => void;
}

interface Warehouse {
  id: string;
  warehouseId: string;
  name: string;
  warehouseName: string;
}

const ProductionPage: React.FC<ProductionPageProps> = ({ onBack }) => {
  const { currentUser } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });

  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [productionData, setProductionData] = useState<ProductionSummaryRow[]>([]);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [pendingChanges, setPendingChanges] = useState<{[productId: string]: number}>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Load warehouses for current user
  const loadWarehouses = async () => {
    try {
      setLoading(true);
      
      // Load warehouses using the exact same pattern as LoadoutPage
      const warehousesData = await getDocuments('warehouses');
      
      // Filter warehouses based on status only (same as LoadoutPage)
      const availableWarehouses = warehousesData.filter((warehouse: any) => warehouse.isActive !== false);
      
      setWarehouses(availableWarehouses as Warehouse[]);
      
    } catch (error) {
      console.error('Error loading warehouses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load production data for selected warehouse and date
  const loadProductionData = async () => {
    if (!selectedWarehouse) return;
    
    try {
      setLoading(true);
      
      const selectedWarehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
      if (!selectedWarehouseDoc || !selectedWarehouseDoc.warehouseId) {
        console.error('Selected warehouse not found or missing warehouseId:', selectedWarehouse);
        return;
      }
      
      const summary = await ProductionService.getWarehouseProductionSummary(
        selectedWarehouseDoc.warehouseId,
        selectedWarehouseDoc.name || selectedWarehouseDoc.warehouseName,
        selectedDate
      );
      
      setProductionData(summary);
      setPendingChanges({}); // Clear pending changes when reloading
      
    } catch (error) {
      console.error('Error loading production data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle cell edit start
  const startEdit = (productId: string, currentValue: number) => {
    setEditingProductId(productId);
    setEditingValue(currentValue.toString());
  };

  // Handle cell edit cancel
  const cancelEdit = () => {
    setEditingProductId(null);
    setEditingValue('');
  };

  // Handle cell edit save
  const saveEdit = () => {
    if (!editingProductId) return;
    
    const newValue = parseInt(editingValue) || 0;
    if (isNaN(newValue) || newValue < 0) {
      alert('Please enter a valid positive number');
      return;
    }
    
    const newPendingChanges = { ...pendingChanges };
    newPendingChanges[editingProductId] = newValue;
    setPendingChanges(newPendingChanges);
    
    // Update local state for immediate UI feedback
    setProductionData(prevData => 
      prevData.map(row => {
        if (row.productId === editingProductId) {
          const carriedForward = row.carriedForward;
          const openingStock = carriedForward + newValue;
          const liveStock = openingStock - row.loadedOut;
          
          return {
            ...row,
            freshProduction: newValue,
            openingStock,
            liveStock,
            hasChanges: true
          };
        }
        return row;
      })
    );
    
    setEditingProductId(null);
    setEditingValue('');
  };

  // Handle confirm all changes
  const confirmChanges = async () => {
    if (Object.keys(pendingChanges).length === 0 || !selectedWarehouse) return;
    
    const selectedWarehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
    if (!selectedWarehouseDoc) return;
    
    setSaving(true);
    setSaveMessage('Saving production changes...');
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const [productId, newQuantity] of Object.entries(pendingChanges)) {
        const productRow = productionData.find(row => row.productId === productId);
        
        if (productRow) {
          const result = await ProductionService.updateProductionQuantity(
            selectedDate,
            selectedWarehouseDoc.warehouseId,
            selectedWarehouseDoc.name || selectedWarehouseDoc.warehouseName,
            productId,
            productRow.productName,
            productRow.unit,
            newQuantity,
            currentUser?.id || 'system'
          );
          
          if (result.success) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to update ${productRow.productName}:`, result.error);
          }
        }
      }
      
      if (errorCount === 0) {
        setSaveMessage(`✅ Successfully updated ${successCount} production entries`);
        setPendingChanges({});
        
        // Reload data to reflect changes
        await loadProductionData();
      } else {
        setSaveMessage(`⚠️ Updated ${successCount} entries, ${errorCount} failed`);
      }
      
    } catch (error) {
      console.error('Error confirming changes:', error);
      setSaveMessage('❌ Error saving changes');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(''), 5000);
    }
  };

  // Load warehouses on component mount
  useEffect(() => {
    loadWarehouses();
  }, [currentUser]);

  // Load production data when warehouse or date changes
  useEffect(() => {
    if (selectedWarehouse) {
      loadProductionData();
    } else {
      // Clear data when no warehouse is selected
      setProductionData([]);
    }
  }, [selectedWarehouse, selectedDate]);

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  if (loading && !selectedWarehouse) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-orange-500" />
            <p className="text-gray-600">Loading warehouses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          
          <div className="flex items-center space-x-3">
            <Factory className="w-6 h-6 text-orange-500" />
            <h1 className="text-2xl font-bold text-gray-800">Production Tracking</h1>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Warehouse Dropdown - Always shown */}
          <div className="min-w-64">
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Warehouse</option>
              {warehouses.map(warehouse => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Date Picker */}
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          
          {/* Confirm Button */}
          <button
            onClick={confirmChanges}
            disabled={!hasPendingChanges || saving}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              hasPendingChanges && !saving
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Confirm Changes'}</span>
            {hasPendingChanges && !saving && (
              <span className="bg-white text-green-600 px-2 py-1 rounded-full text-xs font-medium">
                {Object.keys(pendingChanges).length}
              </span>
            )}
          </button>
          
          {/* Refresh Button */}
          <button
            onClick={loadProductionData}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm">{saveMessage}</p>
        </div>
      )}

      {/* Warehouse Info */}
      {selectedWarehouse && (() => {
        const selectedWarehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
        return selectedWarehouseDoc ? (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-orange-900">{selectedWarehouseDoc.name || selectedWarehouseDoc.warehouseName}</h3>
                <p className="text-sm text-orange-700">Production data for {selectedDate}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-orange-700">{productionData.length} products</p>
                <p className="text-xs text-orange-600">
                  {productionData.filter(p => p.hasChanges).length} modified
                </p>
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {/* Production Table */}
      {productionData.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-auto">
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Header */}
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                    Product
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wider">
                    Carried Forward
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider">
                    Fresh Production
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-orange-600 uppercase tracking-wider">
                    Opening Stock
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-red-600 uppercase tracking-wider">
                    Loaded Out
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Live Stock
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {productionData.map((row, index) => {
                  const isEditing = editingProductId === row.productId;
                  const isEven = index % 2 === 0;
                  
                  return (
                    <tr key={row.productId} className={isEven ? 'bg-white' : 'bg-gray-50'}>
                      {/* Product Name */}
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 sticky left-0 z-10" style={{ backgroundColor: isEven ? 'white' : '#f9fafb' }}>
                        <div>
                          <div className="font-medium">{row.productName}</div>
                          <div className="text-xs text-gray-500">{row.category}</div>
                        </div>
                      </td>
                      
                      {/* Unit */}
                      <td className="px-4 py-3 text-center text-sm text-gray-600">
                        {row.unit}
                      </td>
                      
                      {/* Carried Forward */}
                      <td className="px-4 py-3 text-center text-sm text-blue-700 font-medium">
                        {Math.round(row.carriedForward)}
                      </td>
                      
                      {/* Fresh Production (Editable) */}
                      <td className="px-4 py-3 text-center">
                        {isEditing ? (
                          <div className="flex items-center justify-center space-x-1">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 text-center"
                              autoFocus
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') saveEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                            />
                            <button onClick={saveEdit} className="p-1 text-green-600 hover:text-green-800">
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button onClick={cancelEdit} className="p-1 text-red-600 hover:text-red-800">
                              <AlertCircle className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(row.productId, row.freshProduction)}
                            className={`flex items-center justify-center space-x-1 px-3 py-1 rounded transition-colors ${
                              row.hasChanges 
                                ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border border-yellow-300' 
                                : 'hover:bg-green-100 text-green-700'
                            }`}
                          >
                            <span className="font-medium">{Math.round(row.freshProduction)}</span>
                            <Edit3 className="w-3 h-3 text-gray-400" />
                          </button>
                        )}
                      </td>
                      
                      {/* Opening Stock */}
                      <td className="px-4 py-3 text-center text-sm text-orange-700 font-bold">
                        {Math.round(row.openingStock)}
                      </td>
                      
                      {/* Loaded Out */}
                      <td className="px-4 py-3 text-center text-sm text-red-700 font-medium">
                        {Math.round(row.loadedOut)}
                      </td>
                      
                      {/* Live Stock */}
                      <td className="px-4 py-3 text-center text-sm text-gray-800 font-bold">
                        {Math.round(row.liveStock)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Factory className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h3>
          <p className="text-gray-500">
            {selectedWarehouse 
              ? (() => {
                  const selectedWarehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
                  return `No products found for ${selectedWarehouseDoc?.name || selectedWarehouseDoc?.warehouseName || 'selected warehouse'}`;
                })()
              : 'Please select a warehouse to view production data'
            }
          </p>
        </div>
      )}
      
      {/* Legend */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Calculation Legend</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span><strong>Carried Forward:</strong> Current Inventory + Loaded Out - Previous Production</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span><strong>Fresh Production:</strong> Today's production (editable)</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-500 rounded"></div>
              <span><strong>Opening Stock:</strong> Carried Forward + Fresh Production</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span><strong>Loaded Out:</strong> Today's route allocations</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-600 rounded"></div>
              <span><strong>Live Stock:</strong> Opening Stock - Loaded Out</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-400 rounded"></div>
              <span><strong>Modified:</strong> Unsaved changes (click Confirm to save)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionPage;